import { createContext } from 'react'

export enum OrderTypes {
	ORDERED = 'ORDERED',
	UNORDERED = 'UNORDERED',
}

// ------------------------------------------------------------- LIST ITEM PREFIX HELPERS ------------------------------------------------------------------

// keeps track of the list type, the number of items in the list, and the current level of the list type
export const ListContext = createContext<{
	type: OrderTypes
	level: number
	value: { current: number }
}>({
	type: OrderTypes.ORDERED,
	level: -1,
	value: { current: 1 },
})

// used to generate the correct list item prefix given the current list context
const listIndexGenerator: {
	[orderType in OrderTypes]: ((index: number) => string)[]
} = {
	[OrderTypes.ORDERED]: [
		(number: number) => `${number}.`,
		(number: number) => romanNumeral(number, true),
		alphabetLetter,
	],
	[OrderTypes.UNORDERED]: [() => '•', () => '◦', () => '■'],
}

/**
 * generatePrefix - generate the prefix of the list item
 *
 * @param {OrderTypes} type - the type of the list to generate the prefix for
 * @param {number} level - the level of list
 * @param {number} number - the item number
 *
 * @return {string} the prefix
 */
export function generatePrefix(
	type: OrderTypes,
	level: number,
	number: number
): string {
	const generators = listIndexGenerator[type]
	// @ts-expect-error TS2722 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
	return generators[Math.min(Math.max(0, level), generators.length)](number)
}

// ------------------------------------------------------------- LIST ITEM PREFIX HELPERS ------------------------------------------------------------------

const ALPHABET = 'abcdefghijklmnopqrstuvwxyz'.split('')
const ROMAN_NUMERALS = [
	'',
	'C',
	'CC',
	'CCC',
	'CD',
	'D',
	'DC',
	'DCC',
	'DCCC',
	'CM',
	'',
	'X',
	'XX',
	'XXX',
	'XL',
	'L',
	'LX',
	'LXX',
	'LXXX',
	'XC',
	'',
	'I',
	'II',
	'III',
	'IV',
	'V',
	'VI',
	'VII',
	'VIII',
	'IX',
]

/**
 * alphabetLetter - generate the corresponding letter equivalent to the index.
 *
 * @param {number} number - the number to get the corresponding letter sequence for
 *
 * @return {string} - the prefix for the given index
 */
function alphabetLetter(number: number): string {
	const base26Number = Math.abs(number - 1).toString(ALPHABET.length)
	return (
		base26Number
			.split('')
			.map(base26 => ALPHABET[parseInt(base26, ALPHABET.length)])
			.join('') + '.'
	)
}

/**
 * romanNumeral - generate the corresponding roman numeral equivalent to the index.
 *   From: https://stackoverflow.com/questions/9083037/convert-a-number-into-a-roman-numeral-in-javascript/9083076
 *
 * @param {number} number - the number to get the corresponding letter sequence for
 * @param {boolean} lowerCase - if true, will return lower case string, false returns an upper case string
 *
 * @return {string} - the prefix for the given index
 */
function romanNumeral(number: number, lowerCase: boolean): string {
	const digits = String(number).split('')
	let roman = ''
	let i = 3
	while (i--) {
		const nextDigit = digits.pop()
		if (nextDigit == null) {
			break
		}
		roman = (ROMAN_NUMERALS[+nextDigit + i * 10] || '') + roman
	}
	const value = Array(+digits.join('') + 1).join('M') + roman
	if (!lowerCase) {
		return value
	}
	return value.toLocaleLowerCase() + '.'
}
